<template>
    <div>
        <div v-if="loading" class="flex items-center justify-center py-24">
            <div class="w-8 h-8 rounded-full border-2 border-gray-200 border-t-primary-500 animate-spin"></div>
        </div>

        <template v-else>
        <div class="mb-8">
            <h1 class="text-2xl font-semibold text-gray-800">{{ $t('account.profile.title') }}</h1>
            <p class="text-normal text-gray-400 mt-1">{{ $t('account.profile.description') }}</p>
        </div>

        <div class="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
            <div class="grid grid-cols-2 gap-3">
                <AppInput
                    v-model="form.firstName"
                    :label="$t('account.profile.firstName')"
                    autocomplete="given-name"
                />
                <AppInput
                    v-model="form.lastName"
                    :label="$t('account.profile.lastName')"
                    autocomplete="family-name"
                />
            </div>
            <AppInput
                v-model="form.email"
                :label="$t('account.profile.email')"
                type="email"
                :disabled="true"
                autocomplete="email"
            />
        </div>

        <div class="mt-6 flex justify-end">
            <div class="w-40">
                <AppButton :loading="saving" :disabled="!hasChanged" @click="save">
                    {{ $t('account.profile.save') }}
                </AppButton>
            </div>
        </div>

        <!-- Language -->
        <div class="mt-8">
            <h2 class="text-base font-semibold text-gray-800 mb-1">{{ $t('account.profile.language') }}</h2>
            <p class="text-sm text-gray-400 mb-4">{{ $t('account.profile.languageDescription') }}</p>
            <div class="bg-white rounded-xl border border-gray-200 p-6">
                <div class="w-64">
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('account.profile.preferredLanguage') }}</label>
                    <select
                        v-model="selectedLanguageId"
                        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                        :disabled="languageSaving"
                        @change="onLanguageChange"
                    >
                        <option v-for="lang in supportedLanguages" :key="lang.id" :value="lang.id">
                            {{ lang.native_name }}
                        </option>
                    </select>
                </div>
                <div
                    v-if="showLanguageChangedNotice"
                    class="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
                >
                    <Info class="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{{ $t('account.profile.languageChangedNotice') }}</span>
                </div>
            </div>
        </div>

        <!-- Danger Zone -->
        <div class="mt-8">
            <section class="bg-white rounded-xl border border-red-200 p-6">
                <!-- Step: idle -->
                <template v-if="delStep === 'idle'">
                    <div class="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <h2 class="text-base font-semibold text-red-600">{{ $t('account.profile.dangerZone.deleteTitle') }}</h2>
                            <p class="text-sm text-gray-400 mt-0.5">{{ $t('account.profile.dangerZone.deleteDescription') }}</p>
                        </div>
                        <div class="w-44 flex-shrink-0">
                            <AppButton variant="warning" @click="delStep = 'warning'">
                                {{ $t('account.profile.dangerZone.deleteAction') }}
                            </AppButton>
                        </div>
                    </div>
                </template>

                <!-- Step: warning (irreversibility) -->
                <template v-else-if="delStep === 'warning'">
                    <div class="flex items-start gap-3">
                        <AlertTriangle class="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 class="text-sm font-semibold text-red-600">{{ $t('account.profile.dangerZone.warningTitle') }}</h3>
                            <p class="text-sm text-gray-600 mt-1">{{ $t('account.profile.dangerZone.warningBody') }}</p>
                        </div>
                    </div>
                    <div class="flex justify-end gap-2 mt-5">
                        <div class="w-24">
                            <AppButton variant="white" @click="cancelDeletion">
                                {{ $t('account.profile.dangerZone.cancel') }}
                            </AppButton>
                        </div>
                        <div class="w-44">
                            <AppButton variant="warning" :loading="delSaving" @click="requestDeletionCode">
                                {{ $t('account.profile.dangerZone.warningContinue') }}
                            </AppButton>
                        </div>
                    </div>
                </template>

                <!-- Step: code sent — enter code + destructive confirm -->
                <template v-else-if="delStep === 'code_sent'">
                    <h2 class="text-base font-semibold text-red-600">{{ $t('account.profile.dangerZone.deleteTitle') }}</h2>
                    <p class="text-sm text-gray-500 mb-4 mt-1">{{ $t('account.profile.dangerZone.codeSentInfo') }}</p>
                    <form class="space-y-3" @submit.prevent="confirmDeletion">
                        <div class="w-56">
                            <AppInput
                                v-model="delForm.code"
                                :label="$t('account.profile.dangerZone.code')"
                                autocomplete="one-time-code"
                                inputmode="numeric"
                                maxlength="6"
                                :error="delErrors.code"
                            />
                        </div>
                        <p v-if="delErrors.general" class="text-xs text-error-500">{{ delErrors.general }}</p>
                        <div class="flex items-center justify-between pt-1">
                            <button
                                type="button"
                                class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-40 transition-colors"
                                :disabled="resendCooldown > 0"
                                @click="requestDeletionCode"
                            >
                                {{ resendCooldown > 0 ? $t('account.profile.dangerZone.resendIn', { s: resendCooldown }) : $t('account.profile.dangerZone.resend') }}
                            </button>
                            <div class="flex gap-2">
                                <div class="w-24">
                                    <AppButton variant="white" @click="cancelDeletion">
                                        {{ $t('account.profile.dangerZone.cancel') }}
                                    </AppButton>
                                </div>
                                <div class="w-60">
                                    <AppButton variant="warning" :loading="delSaving" @click="confirmDeletion">
                                        {{ $t('account.profile.dangerZone.confirm') }}
                                    </AppButton>
                                </div>
                            </div>
                        </div>
                    </form>
                </template>
            </section>
        </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { Info, AlertTriangle } from 'lucide-vue-next';
import AppInput from '@/components/ui/AppInput.vue';
import AppButton from '@/components/ui/AppButton.vue';
import { useStore } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { securityApi } from '@/api/security';
import Authentication from '@/plugins/authentication';

const store = useStore();
const toast = useToast();
const { t } = useI18n();
const loading = ref(!store.state.user);
const saving = ref(false);
const languageSaving = ref(false);
const showLanguageChangedNotice = ref(false);

const form = reactive({ firstName: '', lastName: '', email: '' });
const savedForm = reactive({ firstName: '', lastName: '' });
const selectedLanguageId = ref<number | null>(null);

const supportedLanguages = computed(() => store.state.languages.filter((l) => l.is_supported));
const hasChanged = computed(
    () => form.firstName !== savedForm.firstName || form.lastName !== savedForm.lastName,
);

// Populate form reactively — AccountLayout already fetches the profile
const storeUser = computed(() => store.state.user);
watch(storeUser, (user) => {
    if (user) {
        form.firstName = user.firstName ?? '';
        form.lastName = user.lastName ?? '';
        form.email = user.email ?? '';
        savedForm.firstName = form.firstName;
        savedForm.lastName = form.lastName;
        selectedLanguageId.value = user.languageId ?? null;
        loading.value = false;
    }
}, { immediate: true });


async function save() {
    saving.value = true;
    try {
        await store.dispatch(ActionType.UpdateProfile, {
            firstName: form.firstName,
            lastName: form.lastName,
        });
        savedForm.firstName = form.firstName;
        savedForm.lastName = form.lastName;
        toast.success(t('account.profile.saved'));
    } catch {
        toast.error(t('errors.unknown'));
    } finally {
        saving.value = false;
    }
}

async function onLanguageChange() {
    if (selectedLanguageId.value === null) return;
    languageSaving.value = true;
    try {
        await store.dispatch(ActionType.SetLanguage, selectedLanguageId.value);
        showLanguageChangedNotice.value = true;
    } catch {
        toast.error(t('errors.unknown'));
    } finally {
        languageSaving.value = false;
    }
}

// ── Delete account (danger zone) ─────────────────────────────────
// Mirrors the two-step verification-code flow in security/Index.vue.
const delStep = ref<'idle' | 'warning' | 'code_sent'>('idle');
const delForm = reactive({ code: '' });
const delErrors = reactive({ code: '', general: '' });
const delSaving = ref(false);
const resendCooldown = ref(0);
let resendTimer: ReturnType<typeof setInterval> | null = null;

function startResendCooldown() {
    resendCooldown.value = 60;
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0 && resendTimer) {
            clearInterval(resendTimer);
            resendTimer = null;
        }
    }, 1000);
}

// Cancel commits to nothing: reset to idle, clear all state, drop the cooldown.
function cancelDeletion() {
    delStep.value = 'idle';
    delForm.code = '';
    delErrors.code = '';
    delErrors.general = '';
    if (resendTimer) { clearInterval(resendTimer); resendTimer = null; }
    resendCooldown.value = 0;
}

async function requestDeletionCode() {
    delSaving.value = true;
    delErrors.code = '';
    delErrors.general = '';
    try {
        await securityApi.requestAccountDeletionCode();
        delStep.value = 'code_sent';
        delForm.code = '';
        startResendCooldown();
        toast.success(t('account.profile.dangerZone.codeSent'));
    } catch (err: any) {
        if (err?.response?.status === 429) {
            delErrors.general = t('account.profile.dangerZone.rateLimited');
        } else {
            delErrors.general = t('errors.unknown');
        }
    } finally {
        delSaving.value = false;
    }
}

async function confirmDeletion() {
    delErrors.code = '';
    delErrors.general = '';
    if (!delForm.code) { delErrors.code = t('account.profile.dangerZone.required'); return; }

    delSaving.value = true;
    try {
        await securityApi.confirmAccountDeletion(delForm.code);
        toast.success(t('account.profile.dangerZone.success'));
        if (resendTimer) { clearInterval(resendTimer); resendTimer = null; }
        // Local logout: clears auth cookies and redirects to the login page.
        await Authentication.logout();
    } catch (err: any) {
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.error;
        if (status === 400 && errorCode === 'invalid_code') {
            // One-strike: the code is now invalidated — surface that honestly.
            delErrors.code = t('account.profile.dangerZone.invalidCode');
        } else if (status === 409 && errorCode === 'provider_managed_user') {
            delErrors.general = t('account.profile.dangerZone.providerManaged');
        } else {
            delErrors.general = t('errors.unknown');
        }
    } finally {
        delSaving.value = false;
    }
}

onUnmounted(() => {
    if (resendTimer) clearInterval(resendTimer);
});
</script>
